import React, { Component, createRef } from "react";

import BufferedNumberInput from "./bufferedNumberInput/bufferedNumberInput";

import "./cropEditor.css";

const MIN_SIZE = 0.02; // smallest crop edge as a fraction of the image
const COMMIT_INTERVAL = 250; // ms; the map preview is re-rendered at most this often while dragging

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// largest centred rectangle with the aspect ratio of the map (what "Center" crop does)
export function fitRect(imgW, imgH, mapX, mapY) {
  const target = mapX / mapY;
  let w;
  let h;
  if (imgW / imgH > target) {
    h = 1;
    w = (imgH * target) / imgW;
  } else {
    w = 1;
    h = imgW / target / imgH;
  }
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

class CropEditor extends Component {
  state = { liveRect: null };

  boxRef = createRef();
  drag = null;
  lastCommit = 0;

  currentRect() {
    const { rect, uploadedImage, optionValue_mapSize_x, optionValue_mapSize_y } = this.props;
    return this.state.liveRect || rect || fitRect(uploadedImage.width, uploadedImage.height, optionValue_mapSize_x, optionValue_mapSize_y);
  }

  onPointerDown = (e) => {
    const handle = e.target.getAttribute("data-handle");
    if (!handle) return;
    e.preventDefault();
    e.target.setPointerCapture(e.pointerId);
    this.drag = { handle, startX: e.clientX, startY: e.clientY, start: this.currentRect() };
  };

  onPointerMove = (e) => {
    if (!this.drag) return;
    const bounds = this.boxRef.current.getBoundingClientRect();
    const dx = (e.clientX - this.drag.startX) / bounds.width;
    const dy = (e.clientY - this.drag.startY) / bounds.height;
    const rect = this.dragged(this.drag.handle, this.drag.start, dx, dy);
    this.setState({ liveRect: rect });
    const now = performance.now();
    if (now - this.lastCommit > COMMIT_INTERVAL) {
      this.lastCommit = now;
      this.props.onChange(rect);
    }
  };

  onPointerUp = () => {
    if (!this.drag) return;
    this.drag = null;
    const { liveRect } = this.state;
    if (liveRect) this.props.onChange(liveRect);
    this.setState({ liveRect: null });
  };

  dragged(handle, start, dx, dy) {
    let { x, y } = start;
    let x2 = start.x + start.w;
    let y2 = start.y + start.h;
    if (handle === "move") {
      x = clamp(x + dx, 0, 1 - start.w);
      y = clamp(y + dy, 0, 1 - start.h);
      return { x, y, w: start.w, h: start.h };
    }
    if (handle.includes("w")) x = clamp(x + dx, 0, x2 - MIN_SIZE);
    if (handle.includes("e")) x2 = clamp(x2 + dx, x + MIN_SIZE, 1);
    if (handle.includes("n")) y = clamp(y + dy, 0, y2 - MIN_SIZE);
    if (handle.includes("s")) y2 = clamp(y2 + dy, y + MIN_SIZE, 1);
    return { x, y, w: x2 - x, h: y2 - y };
  }

  setSize(key, percent) {
    const r = this.currentRect();
    const size = clamp(percent / 100, MIN_SIZE, 1);
    const pos = key === "w" ? "x" : "y";
    this.props.onChange({ ...r, [key]: size, [pos]: clamp(r[pos], 0, 1 - size) });
  }

  render() {
    const { getLocaleString, uploadedImage, optionValue_mapSize_x, optionValue_mapSize_y, onChange } = this.props;
    const r = this.currentRect();
    // how far the picture gets squeezed when the cropped region is put on the map
    const ratio = (r.w * uploadedImage.width) / (r.h * uploadedImage.height) / (optionValue_mapSize_x / optionValue_mapSize_y);
    let distortion = getLocaleString("MAP-SETTINGS/CROP/PROPORTIONS-KEPT");
    if (ratio > 1.01) distortion = `${getLocaleString("MAP-SETTINGS/CROP/SQUEEZED-H")} ${Math.round((1 - 1 / ratio) * 100)} %`;
    else if (ratio < 0.99) distortion = `${getLocaleString("MAP-SETTINGS/CROP/SQUEEZED-V")} ${Math.round((1 - ratio) * 100)} %`;
    const pct = (v) => `${(v * 100).toFixed(3)}%`;
    return (
      <div className="cropEditor">
        <div className="cropEditorBox" ref={this.boxRef} onPointerDown={this.onPointerDown} onPointerMove={this.onPointerMove} onPointerUp={this.onPointerUp} onPointerCancel={this.onPointerUp}>
          <img className="cropEditorImage" src={uploadedImage.src} alt="" draggable="false" />
          <div className="cropEditorRect" data-handle="move" style={{ left: pct(r.x), top: pct(r.y), width: pct(r.w), height: pct(r.h) }}>
            {HANDLES.map((h) => (
              <div key={h} className={`cropEditorHandle cropEditorHandle-${h}`} data-handle={h} />
            ))}
          </div>
        </div>
        <div className="cropEditorControls">
          <label>
            {getLocaleString("MAP-SETTINGS/CROP/WIDTH")}{" "}
            <BufferedNumberInput
              min="2"
              max="100"
              step="1"
              value={Math.round(r.w * 100)}
              validators={[(t) => !isNaN(t), (t) => t >= 2, (t) => t <= 100]}
              onValidInput={(v) => this.setSize("w", v)}
              style={{ width: "3em" }}
            />
            {" %"}
          </label>
          <label>
            {getLocaleString("MAP-SETTINGS/CROP/HEIGHT")}{" "}
            <BufferedNumberInput
              min="2"
              max="100"
              step="1"
              value={Math.round(r.h * 100)}
              validators={[(t) => !isNaN(t), (t) => t >= 2, (t) => t <= 100]}
              onValidInput={(v) => this.setSize("h", v)}
              style={{ width: "3em" }}
            />
            {" %"}
          </label>
        </div>
        <div className="cropEditorButtons">
          <button type="button" onClick={() => onChange(null)}>
            {getLocaleString("MAP-SETTINGS/CROP/FIT")}
          </button>
          <button type="button" onClick={() => onChange({ x: 0, y: 0, w: 1, h: 1 })}>
            {getLocaleString("MAP-SETTINGS/CROP/WHOLE")}
          </button>
        </div>
        <small className="cropEditorInfo">{distortion}</small>
        <small className="cropEditorInfo">{getLocaleString("MAP-SETTINGS/CROP/EDITOR-HINT")}</small>
      </div>
    );
  }
}

export default CropEditor;
